import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import AiScoreBadge from "../../components/AiScoreBadge";
import StatusBadge from "../../components/StatusBadge";
import NotificationBell from "../../components/NotificationBell";
import LanguageToggle from "../../components/LanguageToggle";
import DocumentsPanel from "../../components/DocumentsPanel";
import RequestedDocsBanner from "../../components/RequestedDocsBanner";
import PreApprovalReadOnly from "../../components/PreApprovalReadOnly";
import Logo from "../../components/Logo";
import { Building2, LogOut } from "lucide-react";
import { fmtCurrency, fmtDate, LOAN_TYPE_LABELS } from "../../lib/constants";
import { toast } from "sonner";

export default function BorrowerDashboard() {
  const { user, logout } = useAuth();
  const [apps, setApps] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/borrower/applications");
        setApps(data);
      } catch (e) { toast.error(errorMessage(e)); }
    })();
  }, []);

  const doLogout = async () => { await logout(); nav("/login"); };

  return (
    <div className="portal-theme min-h-screen bg-slate-50 text-slate-900">
      <header className="sticky top-0 z-20 flex h-20 items-center justify-between border-b border-slate-200 bg-white/95 backdrop-blur-sm px-6">
        <div className="flex items-center gap-2">
          <Logo height={56} />
        </div>
        <div className="flex items-center gap-3">
          <LanguageToggle />
          <NotificationBell role="borrower" onNavigate={() => {}} />
          <Button variant="ghost" onClick={doLogout} className="text-slate-600 hover:bg-slate-100" data-testid="bwr-logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 animate-fade-in-up">
        <div className="mb-8">
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Welcome, {user?.name}</h1>
          <p className="mt-1 text-sm text-slate-500">Track your application status and uploads.</p>
        </div>

        <div className="space-y-4">
          {apps.length === 0 && (
            <Card className="rounded-sm border-slate-200 bg-white p-8 text-center text-sm text-slate-500 shadow-none" data-testid="bwr-empty-card">
              You don&apos;t have any applications on file yet.
            </Card>
          )}
          {apps.map((a) => (
            <Card key={a.application_id} className="rounded-sm border-slate-200 bg-white p-6 shadow-none" data-testid={`bwr-app-${a.application_id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-slate-900">{LOAN_TYPE_LABELS[a.loan_type] || a.loan_type}</div>
                  <div className="text-sm text-slate-500">{a.property_address}</div>
                </div>
                <div className="flex items-center gap-2">
                  <AiScoreBadge score={a.ai_score} testId={`bwr-score-${a.application_id}`} />
                  <StatusBadge status={a.status} />
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                <DT label="Loan Amount" value={fmtCurrency(a.expected_loan_amount)} />
                <DT label="Purchase" value={fmtCurrency(a.purchase_price)} />
                <DT label="Rehab" value={fmtCurrency(a.estimated_rehab_budget)} />
                <DT label="Submitted" value={fmtDate(a.created_at)} />
              </div>
              {(a.requested_docs || []).length > 0 && (
                <div className="mt-6">
                  <RequestedDocsBanner
                    applicationId={a.application_id}
                    requestedDocs={a.requested_docs}
                  />
                </div>
              )}
              <div className="mt-6">
                <DocumentsPanel
                  applicationId={a.application_id}
                  canUpload={true}
                  title="Documents"
                  subtitle={a.status === "docs_requested" ? "Documents requested — please upload below." : "Upload any supporting documents."}
                />
              </div>
              <div className="mt-6">
                <PreApprovalReadOnly applicationId={a.application_id} />
              </div>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}

function DT({ label, value }) {
  return (
    <div className="rounded-sm border border-slate-200 bg-slate-50 p-3">
      <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
      <div className="mt-1 text-slate-900">{value}</div>
    </div>
  );
}
